import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  Slide
} from '@/ui';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import type { TransitionProps } from '@mui/material/transitions';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ContentItem {
  text: string;
  isHighlight?: boolean;
  isLink?: boolean;
  color?: string;
}

interface ReviewSectionContent {
  items: ContentItem[];
  hasNote?: boolean;
}

interface ReviewSection {
  title: string;
  content: ReviewSectionContent[];
  onEdit: () => void;
}

interface ReviewBudgetSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  sections?: ReviewSection[];
}

const defaultSections: ReviewSection[] = [
  {
    title: 'Income',
    content: [
      {
        items: [
          { text: 'Is ' },
          { text: '£150 a month', isHighlight: true },
          { text: ' for take home salary is correct ? or ' },
          { text: 'add a Note', isLink: true, color: '#0891B2' },
          { text: ' to explain.' }
        ],
        hasNote: true
      }
    ],
    onEdit: () => console.log('Edit Income')
  },
  {
    title: 'Assets',
    content: [
      {
        items: [
          { text: 'Based on the data you provided, you mentioned information about vehicles. However, some of the vehicle-related questions may be missing data. These questions are related to vehicles—please review them and fill in any missing information.' }
        ],
        hasNote: true
      }
    ],
    onEdit: () => console.log('Edit Assets')
  },
  {
    title: 'Fixed expenses',
    content: [
      {
        items: [
          { text: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum is simply dummy text of the printing and typesetting industry.' }
        ],
        hasNote: true
      }
    ],
    onEdit: () => console.log('Edit fixed expenses')
  }
];

const ReviewBudgetSummaryDialog: React.FC<ReviewBudgetSummaryDialogProps> = ({
  open,
  onClose,
  sections = defaultSections
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const renderContentItem = (item: ContentItem, index: number) => {
    const baseStyles = {
      fontSize: isMobile ? '13px' : '14px',
      lineHeight: 1.6,
      color: item.color || '#1F2937',
      fontWeight: item.isHighlight ? 600 : 400,
      textDecoration: item.isLink ? 'underline' : 'none',
      cursor: item.isLink ? 'pointer' : 'default',
      '&:hover': item.isLink ? { opacity: 0.8 } : undefined
    };

    return (
      <Typography
        key={index}
        component="span"
        sx={baseStyles}
      >
        {item.text}
      </Typography>
    );
  };

  const renderContentBlock = (content: ReviewSectionContent, index: number) => (
    <Box
      key={index}
      sx={{
        mb: content.hasNote ? 1.5 : 1
      }}
    >
      <Box
        sx={{
          borderLeft: '4px solid #DC2626',
          pl: 1.5,
          py: 0.5
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: isMobile ? '13px' : '14px',
            lineHeight: 1.6
          }}
        >
          {content.items.map(renderContentItem)}
        </Typography>
      </Box>
      {content.hasNote && (
        <Typography
          variant="body2"
          sx={{
            color: '#9CA3AF',
            fontSize: isMobile ? '12px' : '13px',
            mt: 1,
            pl: 1.5
          }}
        >
          + Notes (optional)
        </Typography>
      )}
    </Box>
  );

  const renderSection = (section: ReviewSection) => (
    <Paper
      key={section.title}
      sx={{
        mb: 2,
        p: isMobile ? 2 : 2.5,
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        boxShadow: 'none'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#0D4E5E',
            fontSize: isMobile ? '15px' : '16px'
          }}
        >
          {section.title}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={section.onEdit}
          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            px: 1.5,
            py: 0.5,
            minWidth: 'auto',
            borderRadius: '6px',
            borderColor: '#D1D5DB',
            color: '#374151',
            '&:hover': {
              backgroundColor: '#F9FAFB',
              borderColor: '#D1D5DB'
            }
          }}
        >
          Edit
        </Button>
      </Box>
      {section.content.map(renderContentBlock)}
    </Paper>
  );

  const dialogContent = (
    <>
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="body2"
          sx={{
            color: '#6B7280',
            fontSize: isMobile ? '13px' : '14px',
            lineHeight: 1.6
          }}
        >
          Please review the information you've provided. You can edit any section before continuing to the next part of your budget.
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        {sections.map(renderSection)}
      </Box>

      <Box
        sx={{
          textAlign: 'center',
          py: 2,
          borderTop: '1px solid #E5E7EB'
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: '#6B7280',
            fontSize: isMobile ? '12px' : '13px'
          }}
        >
          Need to make changes? Click the "Edit" button next to any section.
        </Typography>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        fullScreen
        PaperProps={{
          sx: {
            borderRadius: '24px 24px 0 0',
            maxHeight: '90vh',
            mt: 'auto'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            p: 2.5,
            pb: 2,
            borderBottom: '1px solid #E5E7EB'
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#0D4E5E',
                fontSize: '18px',
                lineHeight: 1.4
              }}
            >
              Review Your Budget Summary
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: '#6B7280',
              mt: -0.5
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, pt: 2 }}>
          {dialogContent}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '85vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pb: 2.5,
          borderBottom: '1px solid #E5E7EB'
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: '#0D4E5E',
            fontSize: '20px',
            lineHeight: 1.4
          }}
        >
          Review Your Budget Summary
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: '#6B7280'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 2.5 }}>
        {dialogContent}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewBudgetSummaryDialog;


